import type { ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SortableRowProps {
	id: number;
	focused?: boolean;
	gridCols: string;
	onClick: () => void;
	children: ReactNode;
}

/** Drag-sortable grid row. The handle is column 1; `children` are the 6 data cells. */
export function SortableRow({ id, focused, gridCols, onClick, children }: SortableRowProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
	const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined };

	return (
		<div
			ref={setNodeRef}
			style={style}
			onClick={onClick}
			className={cn(
				'grid cursor-pointer items-center gap-2 border-b border-border-subtle px-4 py-2',
				gridCols,
				focused ? 'bg-brand-subtle' : 'hover:bg-muted',
				isDragging && 'opacity-80'
			)}>
			<button
				type="button"
				{...attributes}
				{...listeners}
				onClick={e => e.stopPropagation()}
				className="flex cursor-grab items-center justify-center text-foreground-subtle hover:text-foreground"
				aria-label="drag">
				<GripVertical size={16} />
			</button>
			{children}
		</div>
	);
}
